import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface PenNamesPageProps {
  onNavigate: (page: string, data?: any) => void;
  user: any;
}

export default function PenNamesPage({ onNavigate, user }: PenNamesPageProps) {
  const [penNames, setPenNames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ penName: '', bio: '', avatarUrl: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    if (!user) return;
    api.penNames.list()
      .then(setPenNames)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.penNames.create({
        penName: form.penName,
        bio: form.bio || undefined,
        avatarUrl: form.avatarUrl || undefined,
      });
      setPenNames(prev => [...prev, { ...created, _count: { stories: 0 } }]);
      setShowCreate(false);
      setForm({ penName: '', bio: '', avatarUrl: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to create pen name');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await api.penNames.update(editingId, {
        bio: form.bio || undefined,
        avatarUrl: form.avatarUrl || undefined,
      });
      setPenNames(prev => prev.map(p => p.id === editingId ? { ...p, ...updated } : p));
      setEditingId(null);
      setForm({ penName: '', bio: '', avatarUrl: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to update pen name');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.penNames.delete(id);
      setPenNames(prev => prev.filter(p => p.id !== id));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete pen name');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (penName: any) => {
    setEditingId(penName.id);
    setForm({ penName: penName.penName, bio: penName.bio || '', avatarUrl: penName.avatarUrl || '' });
    setShowCreate(false);
    setError(null);
  };

  const cancelForm = () => {
    setShowCreate(false);
    setEditingId(null);
    setForm({ penName: '', bio: '', avatarUrl: '' });
    setError(null);
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Sign in to manage pen names</h2>
        <button onClick={() => onNavigate('login')} className="px-6 py-3 bg-ink-600 rounded-xl font-semibold">
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Pen Names</h1>
          <p className="text-gray-400 mt-1">Manage your author identities. Stories published under a pen name stay separate from your main profile.</p>
        </div>
        {!showCreate && !editingId && penNames.length < 3 && (
          <button
            onClick={() => { setShowCreate(true); setError(null); }}
            className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors shrink-0"
          >
            + New Pen Name
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6">
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full ${i < penNames.length ? 'bg-ink-500' : 'bg-gray-700'}`}
            />
          ))}
        </div>
        <span className="text-sm text-gray-400">{penNames.length}/3 pen names used</span>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400 text-sm">
          {error}
        </div>
      )}

      {(showCreate || editingId) && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">{editingId ? 'Edit Pen Name' : 'Create Pen Name'}</h2>
          <div className="space-y-4">
            {!editingId && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Pen Name *</label>
                <input
                  value={form.penName}
                  onChange={e => setForm(f => ({ ...f, penName: e.target.value }))}
                  placeholder="Your pen name"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:border-ink-500 focus:outline-none"
                  maxLength={50}
                />
              </div>
            )}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Bio</label>
              <textarea
                value={form.bio}
                onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                placeholder="A short bio for this pen name"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 h-24 resize-none focus:border-ink-500 focus:outline-none"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{form.bio.length}/500</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Avatar URL</label>
              <input
                value={form.avatarUrl}
                onChange={e => setForm(f => ({ ...f, avatarUrl: e.target.value }))}
                placeholder="https://example.com/avatar.png"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:border-ink-500 focus:outline-none"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={cancelForm} className="px-4 py-2 text-gray-400 hover:text-white transition-colors">
                Cancel
              </button>
              <button
                onClick={editingId ? handleUpdate : handleCreate}
                disabled={(!editingId && !form.penName.trim()) || submitting}
                className="px-6 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg font-medium disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Saving...' : editingId ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : penNames.length === 0 && !showCreate ? (
        <div className="text-center py-20 bg-gray-900/50 rounded-2xl border border-gray-800">
          <span className="text-5xl block mb-4">🎭</span>
          <h2 className="text-2xl font-bold mb-2">No pen names yet</h2>
          <p className="text-gray-400 mb-6">Create a pen name to publish stories under a different identity.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
          >
            Create Your First Pen Name
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {penNames.map(pn => (
            <div key={pn.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {pn.avatarUrl ? (
                    <img src={pn.avatarUrl} alt={pn.penName} className="w-12 h-12 rounded-full object-cover border border-gray-700" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-ink-600/20 border border-ink-500/30 flex items-center justify-center text-ink-400 font-bold text-lg">
                      {pn.penName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold">{pn.penName}</h3>
                    {pn.bio && <p className="text-gray-400 text-sm mt-1">{pn.bio}</p>}
                    <div className="flex gap-4 text-sm text-gray-500 mt-2">
                      <span>{pn._count?.stories || 0} stories</span>
                      <span>Created {new Date(pn.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onNavigate('pen-profile', { penName: pn.penName })}
                    className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    View
                  </button>
                  <button
                    onClick={() => startEdit(pn)}
                    className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    Edit
                  </button>
                  {(pn._count?.stories || 0) === 0 ? (
                    deleteConfirm === pn.id ? (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleDelete(pn.id)}
                          disabled={submitting}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-lg text-sm transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(pn.id)}
                        className="px-3 py-1.5 bg-red-900/50 hover:bg-red-800 text-red-400 hover:text-red-300 rounded-lg text-sm transition-colors"
                      >
                        Delete
                      </button>
                    )
                  ) : (
                    <span className="px-3 py-1.5 text-gray-600 text-sm cursor-not-allowed" title="Cannot delete: has linked stories">
                      Delete
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
